import React, { useContext, useMemo, useState } from 'react';
import './UploadForm.css';
import { Helmet } from 'react-helmet';

import ResultsDisplay from './results/ResultCardPopUp.js';
import FilePreviewModal from './preview/FilePreviewModal.js';

import { AppContext } from '../context/appContext.js';
import useConfetti from '../hooks/useConfetti.js';
import useJobTitleMatch from '../hooks/useJobTitleMatch.js';
import useFileUpload from '../hooks/useFileUpload.js';
import useKeywords from '../hooks/useKeywords.js';
import useUploadSubmit from '../hooks/useUploadSubmit.js';

// Extracted components
import UploadHero from './upload/UploadHero';
import UploadHeader from './upload/UploadHeader';
import DropArea from './upload/DropArea';
import DropAreaLower from './upload/DropAreaLower';
import UploadedFilesGrid from './upload/UploadedFilesGrid';
import UploadedResumeSummary from './upload/UploadedResumeSummary';
import SubmitButton from './upload/SubmitButton';
import ConfettiOverlay from './upload/ConfettiOverlay';
import LoadingOverlay from './upload/LoadingOverlay';

import JobTitleInput from './job/JobTitleInput';
import JobDescriptionInput from './job/JobDescriptionInput';

import KeywordUtilities from './keywords/KeywordUtilities';
import KeywordAddInput from './keywords/KeywordAddInput';
import KeywordsList from './keywords/KeywordsList';
import ShowMoreKeywordsButton from './keywords/ShowMoreKeywordsButton';



function UploadForm() {
  // Core state
  const [jobDescription, setJobDescription] = useState('');
  const [responseJson, setResponseJson] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const maxResumes = 8;

  // Confetti
  const { uploadContainerRef, showConfetti, confettiSize, confettiOpacity, triggerConfetti } = useConfetti();

  // Job title matching
  const { jobTitle, setJobTitle, hasDictionaryMatch } = useJobTitleMatch('');

  // File upload & previews
  const {
    bulkFiles,
    isDragging,
    fileInputRef,
    editingFilename,
    tempFilename,
    previewFile,
    previewFileIndex,
    numPages,
    pageNumber,
    editedFiles,
    pendingFilenameInputs,
    submittedFilenameInputs,
    isFirstUpload,
    setPreviewFile,
    setPreviewFileIndex,
    setNumPages,
    setPageNumber,
    setEditingFilename,
    setTempFilename,
    setSubmittedFilenameInputs,
    setEditedFiles,
    setBulkFiles,
    setFirstUpload,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleClick,
    handleRemoveFile,
    handleEdit,
    handleBulkChange,
  } = useFileUpload({ maxResumes, onError: setUploadError });

  // Derived
  const [rankedResumesWithOriginalIndex, setRankedResumesWithOriginalIndex] = useState([]);
  const [previewCache, setPreviewCache] = useState([]);
  const [previewCacheStatus, setPreviewCacheStatus] = useState(false);
  const [activePreviewIndex, setActivePreviewIndex] = useState(null);

  const clearPreviewCache = () => {
    setPreviewCache([]);
    setPreviewCacheStatus(false);
    setActivePreviewIndex(null);
    setPreviewFile(null);
  };

  // Keywords
  const {
    editableKeywords,
    setEditableKeywords,
    newKeyword,
    setNewKeyword,
    newKeywords,
    keywordFeedback,
    setKeywordFeedback,
    keywordsLocked,
    setKeywordsLocked,
    keywordMethods,
    useSpacyNext,
    setUseSpacyNext,
    currentMethodIndex,
    isLoadingMoreKeywords,
    shouldHideButton,
    copyStatus,
    handleRemoveKeyword,
    clearAllKeywords,
    copyKeywordsToClipboard,
    handleAddKeyword,
    loadMoreKeywords,
    reanalyzeWithUpdatedKeywords,
    toggleKeywordsLock,
  } = useKeywords({
    responseJson,
    setResponseJson,
    rankedResumesWithOriginalIndex,
    setRankedResumesWithOriginalIndex,
    setIsLoading,
    onConfetti: triggerConfetti,
  });

  // Submit
  const { handleSubmit, processingProgress, currentCyclingIcon } = useUploadSubmit({
    jobTitle,
    jobDescription,
    keywordsLocked,
    editableKeywords,
    bulkFiles,
    pendingFilenameInputs,
    submittedFilenameInputs,
    setSubmittedFilenameInputs,
    setEditedFiles,
    setResponseJson,
    setRankedResumesWithOriginalIndex,
    setUploadError,
    isLoading,
    setIsLoading,
    triggerConfetti,
    clearPreviewCache,
  });

  // App context
  const { setIsPopUp } = useContext(AppContext);
  const togglePopUp = () => setIsPopUp((prev) => !prev);

  const previewMatchedKeywords = useMemo(() => {
    if (!previewFile) return [];
    const match = responseJson?.ranked_resumes?.find((r) => r.filename === previewFile.name);
    return match?.matching_keywords || [];
  }, [previewFile, responseJson]);


  return (
    <>
          <Helmet>
            <title>Upload</title>
          </Helmet>
          <div className="upload-container" ref={uploadContainerRef} aria-busy={isLoading || undefined}>
            <ConfettiOverlay show={showConfetti} width={confettiSize.width} height={confettiSize.height} opacity={confettiOpacity} />
            <LoadingOverlay
              show={isLoading}
              message="Processing resumes…"
              progress={processingProgress}
              currentCyclingIcon={currentCyclingIcon}
              //showSmallAnimation={!!responseJson?.job_description_keywords}
            />

        {previewFile && (
          <FilePreviewModal
            file={previewFile}
            onClose={() => {
              setPreviewFile(null);
            }}
            embedded={false}
            matchedKeywords={previewMatchedKeywords}
            editedName={
              previewFileIndex != null
                ? (() => {
                    const idx = previewFileIndex;
                    const fileObj = bulkFiles[idx];
                    if (!editedFiles[idx]) return fileObj.name;
                    const base = pendingFilenameInputs[idx] ?? fileObj.name.replace(/\.[^/.]+$/, '');
                    return base;
                  })()
                : previewFile.name
            }
            pageNumber={pageNumber}
            numPages={numPages}
            onPageChange={setPageNumber}
            onDocumentLoad={({ numPages }) => setNumPages(numPages)}
          />
        )}

        <UploadHero />
        <UploadHeader />

        <form className="upload-form" onSubmit={handleSubmit}>
          <div className="upload-form-top">
            {bulkFiles.length === 0 && isFirstUpload && (
              <DropArea
                isDragging={isDragging}
                onClick={handleClick}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                fileInputRef={fileInputRef}
                onFilesChange={handleBulkChange}
                disabled={isLoading}
              />
            )}
          </div>

          <JobTitleInput
            jobTitle={jobTitle}
            onChange={(e) => {
              setJobTitle(e.target.value);
            }}
            hasDictionaryMatch={hasDictionaryMatch}
          />

          <JobDescriptionInput
            keywordsLocked={keywordsLocked}
            value={jobDescription}
            onChange={(text) => setJobDescription(text)}
            onError={(msg) => setUploadError(msg || '')}
          />

          <div className={`uploaded-resume-list-container ${isFirstUpload ? 'hidden' : ''}`}>
            {bulkFiles.length > 0 && !isFirstUpload && (
              <UploadedFilesGrid
                files={bulkFiles}
                editedFlags={editedFiles}
                pendingInputs={pendingFilenameInputs}
                editingIndex={editingFilename}
                tempName={tempFilename}
                setEditingIndex={setEditingFilename}
                setTempName={setTempFilename}
                onEdit={handleEdit}
                onRemove={handleRemoveFile}
                onPreview={(index) => {
                  const file = bulkFiles[index];
                  setPreviewFile(file);
                  setPreviewFileIndex(index);
                  togglePopUp();
                }}
                disabled={isLoading}
              />
            )}

            {!isFirstUpload && (
              <DropAreaLower
                isDragging={isDragging}
                onClick={handleClick}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                fileInputRef={fileInputRef}
                onFilesChange={handleBulkChange}
                disabled={isLoading}
              />
            )}
          </div>

          {bulkFiles.length > 0 && <UploadedResumeSummary files={bulkFiles} editedFlags={editedFiles} disabled={isLoading} />}

          <div style={{ marginTop: '0px', display: 'flex', alignItems: 'center' }}>
            <div className="submit-wrapper">
              <SubmitButton isLoading={isLoading} progress={processingProgress} disabled={isLoading} />
            </div>
          </div>

          {uploadError && (
            <p style={{ color: 'red', marginTop: '20px', textAlign: 'left' }}>{uploadError}</p>
          )}
        </form>

        {responseJson && (
          <div className="keywords-summary-section">
            <KeywordUtilities
              keywordsLocked={keywordsLocked}
              onToggleLock={toggleKeywordsLock}
              onClearAll={clearAllKeywords}
              onCopy={() => copyKeywordsToClipboard(editableKeywords)}
              copyStatus={copyStatus}
            />

            <div className="keyword-section-header">
              <div className="keyword-count-wrapper">
                <h3>
                  <span id="keyword-count-number">{editableKeywords.length}</span>
                  {' Keywords'}
                </h3>
              </div>
              {keywordFeedback.message && (
                <p className={`keyword-feedback ${keywordFeedback.isError ? 'error' : 'system'}`}>{keywordFeedback.message}</p>
              )}
            </div>

            <KeywordAddInput
              value={newKeyword}
              onChange={(val) => {
                setNewKeyword(val);
                if (keywordFeedback.message) setKeywordFeedback({ message: '', isError: false });
              }}
              onAdd={handleAddKeyword}
            />

            <KeywordsList
              editableKeywords={editableKeywords}
              rankedResumes={responseJson.ranked_resumes}
              newKeywordsMap={newKeywords}
              onRemoveKeyword={handleRemoveKeyword}
            />

            <ShowMoreKeywordsButton
              onClick={loadMoreKeywords}
              disabled={isLoadingMoreKeywords}
              isLoading={isLoadingMoreKeywords}
              shouldShow={!shouldHideButton || useSpacyNext}
            />
          </div>
        )}

        {rankedResumesWithOriginalIndex.length > 0 && (
          <ResultsDisplay
            rankedResumes={rankedResumesWithOriginalIndex}
            jobDescriptionKeywords={editableKeywords}
            onRemoveKeyword={handleRemoveKeyword}
            editedFilenameInputs={submittedFilenameInputs}
            previewCache={previewCache}
            setPreviewCache={setPreviewCache}
            previewCacheStatus={previewCacheStatus}
            setPreviewCacheStatus={setPreviewCacheStatus}
            activePreviewIndex={activePreviewIndex}
            setActivePreviewIndex={setActivePreviewIndex}
            clearPreviewCache={clearPreviewCache}
          />
        )}
      </div>
    </>
  );
}

export default UploadForm;


